<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import type { CreateTestResultRequest, LanguageTestResult } from '@/services/iliasService'

import { toAppColor } from '@/composables/useAppColor'
import { useAppToast } from '@/composables/useAppToast'
import { iliasService } from '@/services/iliasService'
import { LanguageLevel } from '@/types'
import { Languages, OrderedLanguages } from '@/types/languages'

definePage({
  meta: {
    requiresAuth: true,
    requiresAdmin: true,
  },
})

// Composables
const toast = useAppToast()
const { d, t } = useI18n()

// Local state for data management
const testResults = ref<LanguageTestResult[]>([])
const allResults = ref<LanguageTestResult[]>([]) // Store all data for client-side filtering
const isLoading = ref(false)
const isSyncing = ref(false)
const isSaving = ref(false)
const isDeleting = ref(false)

// Table state
const searchFilter = ref('')
const eligibleLevelFilter = ref<LanguageLevel | null>(null)
const sortField = ref<string>('userName')
const sortOrder = ref<'asc' | 'desc'>('asc')

// Every spoken language the backend knows. A test result is always for one language, and
// this page previously had no notion of that at all -- it sent creates without one.
const languageOptions = computed(() =>
  OrderedLanguages.map((language) => ({
    label: t(`enums.spokenLanguage.${language}`),
    value: language,
  })),
)

// Restore language level options and helpers
const languageLevelOptions = computed(() => [
  { label: 'A1', value: LanguageLevel.A1 },
  { label: 'A2', value: LanguageLevel.A2 },
  { label: 'B1', value: LanguageLevel.B1 },
  { label: 'B2', value: LanguageLevel.B2 },
  { label: 'C1', value: LanguageLevel.C1 },
  { label: 'C2', value: LanguageLevel.C2 },
])

const getScoreClass = (percent?: number | null): string => {
  if (percent == null) {
    return 'text-gray-400 dark:text-gray-500'
  }
  if (percent >= 90) {
    return 'text-emerald-500 dark:text-emerald-400' // C2
  }
  if (percent >= 80) {
    return 'text-green-500 dark:text-green-400' // C1
  }
  if (percent >= 65) {
    return 'text-blue-500 dark:text-blue-400' // B2
  }
  if (percent >= 50) {
    return 'text-yellow-500 dark:text-yellow-400' // B1
  }
  if (percent >= 20) {
    return 'text-orange-500 dark:text-orange-400' // A2
  }
  return 'text-red-500 dark:text-red-400' // A1 or below
}

const getEligibleLevels = (result: LanguageTestResult): LanguageLevel[] => {
  // The backend computes this with the same ranges and source rules used during registration.
  // backend-enums.ts asserts that the generated string union matches the runtime enum.
  return result.eligibleLevels as LanguageLevel[]
}

const getEligibleLevelsLabel = (result: LanguageTestResult): string => {
  return getEligibleLevels(result)
    .map((level) => level)
    .join(', ')
}

const createSortFunction = (field: keyof LanguageTestResult, order: 'asc' | 'desc') => {
  const direction = order === 'asc' ? 1 : -1
  return (a: LanguageTestResult, b: LanguageTestResult): number => {
    const aValue: any = a[field]
    const bValue: any = b[field]
    if (aValue == null && bValue == null) {
      return 0
    }
    if (aValue == null) {
      return direction
    }
    if (bValue == null) {
      return -direction
    }
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return aValue.toLowerCase().localeCompare(bValue.toLowerCase()) * direction
    }
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return (aValue - bValue) * direction
    }
    if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
      return (aValue === bValue ? 0 : aValue ? 1 : -1) * direction
    }
    if (aValue < bValue) {
      return -1 * direction
    }
    if (aValue > bValue) {
      return 1 * direction
    }
    return 0
  }
}

// Sync status
const syncMessage = ref<string | null>(null)
const syncSuccess = ref(false)

// Edit dialog state
const editDialogVisible = ref(false)
const editingResult = ref<LanguageTestResult | null>(null)
// The grade field only drives eligibility for non-ILIAS records; ILIAS records are scored by
// percent, so editing a grade there would have no effect.
const isEditingGradeBacked = computed(() => editingResult.value?.source !== 'ilias')

// Delete dialog state
const deleteDialogVisible = ref(false)
const deletingResult = ref<LanguageTestResult | null>(null)

// Create manual entry dialog state
const createDialogVisible = ref(false)
const newResult = ref<CreateTestResultRequest>({
  userName: '',
  matriculationNumber: '',
  percent: null,
  grade: null,
  language: Languages.English,
  source: 'manual',
})
const isCreating = ref(false)

// The API models an absent matriculation number as null; UInput models it as ''. These bridge
// the two so an emptied field clears the value rather than storing a blank string.
const editMatriculationNumber = computed({
  get: () => editingResult.value?.matriculationNumber ?? '',
  set: (value: string) => {
    if (editingResult.value) {
      editingResult.value.matriculationNumber = value.trim() === '' ? null : value
    }
  },
})

const newMatriculationNumber = computed({
  get: () => newResult.value.matriculationNumber ?? '',
  set: (value: string) => {
    newResult.value.matriculationNumber = value.trim() === '' ? null : value
  },
})

// Computed properties
const filteredResults = computed(() => {
  let filtered = testResults.value
  if (searchFilter.value) {
    const searchTerm = searchFilter.value.toLowerCase()
    filtered = filtered.filter((result) => {
      return (
        (result.userName && result.userName.toLowerCase().includes(searchTerm)) ||
        (result.matriculationNumber &&
          result.matriculationNumber.toLowerCase().includes(searchTerm))
      )
    })
  }
  if (eligibleLevelFilter.value) {
    filtered = filtered.filter((result) =>
      getEligibleLevels(result).includes(eligibleLevelFilter.value!),
    )
  }
  return filtered
})

// Separate computed for sorted data
const filteredAndSortedResults = computed(() => {
  const filtered = [...filteredResults.value] // Shallow copy to avoid mutating original

  // Use optimized sort function
  const sortFn = createSortFunction(sortField.value as keyof LanguageTestResult, sortOrder.value)
  return filtered.sort(sortFn)
})

// Sorting functionality
function toggleSort(field: keyof LanguageTestResult) {
  if (sortField.value === field) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortField.value = field
    sortOrder.value = 'asc'
  }
}

// Methods
async function loadResults() {
  isLoading.value = true
  try {
    // Load everything and filter client-side
    const response = await iliasService.getTestResults()
    testResults.value = response
    allResults.value = response
  } catch (error) {
    console.error('Error loading test results:', error)
    toast.add({
      color: 'error',
      title: 'Error',
      description: 'Failed to load test results',
      duration: 5000,
    })
  } finally {
    isLoading.value = false
  }
}

async function syncData() {
  isSyncing.value = true
  syncMessage.value = null

  try {
    const response = await iliasService.syncData()
    syncSuccess.value = response.success
    syncMessage.value = response.message

    if (response.success) {
      toast.add({
        color: 'success',
        title: 'Sync Queued',
        description: `Queued ${response.count} sync job(s). Results appear here as each one finishes.`,
        duration: 5000,
      })

      // Deliberately no reload: the jobs have not run yet, so refetching now would show the
      // pre-sync results and read as "the sync did nothing". The admin reloads when ready.
    }
  } catch (error) {
    console.error('Error syncing data:', error)
    syncSuccess.value = false
    syncMessage.value = error instanceof Error ? error.message : 'Sync failed'

    toast.add({
      color: 'error',
      title: 'Sync Failed',
      description: 'Failed to sync ILIAS data',
      duration: 5000,
    })
  } finally {
    isSyncing.value = false
  }
}

function editResult(result: LanguageTestResult) {
  editingResult.value = { ...result }
  editDialogVisible.value = true
}

function openCreateDialog() {
  // Reset form
  newResult.value = {
    userName: '',
    matriculationNumber: '',
    percent: null,
    grade: null,
    language: Languages.English,
    source: 'manual',
  }
  createDialogVisible.value = true
}

function confirmDelete(result: LanguageTestResult) {
  deletingResult.value = result
  deleteDialogVisible.value = true
}

async function saveResult() {
  if (!editingResult.value) {
    return
  }

  if (editingResult.value.source !== 'ilias' && !editingResult.value.grade) {
    toast.add({
      color: 'warning',
      title: 'Missing Grade',
      description: 'Grade is required for manual entries',
      duration: 4000,
    })
    return
  }

  isSaving.value = true

  try {
    await iliasService.updateTestResult(editingResult.value.id, {
      matriculationNumber: editingResult.value.matriculationNumber,
      percent: editingResult.value.percent,
      grade: editingResult.value.grade,
      language: editingResult.value.language,
    })

    toast.add({
      color: 'success',
      title: 'Updated',
      description: 'Test result updated successfully',
      duration: 3000,
    })

    editDialogVisible.value = false
    await loadResults()
  } catch (error) {
    console.error('Error updating test result:', error)
    toast.add({
      color: 'error',
      title: 'Error',
      description: 'Failed to update test result',
      duration: 5000,
    })
  } finally {
    isSaving.value = false
  }
}

async function createResult() {
  if (!newResult.value.grade) {
    toast.add({
      color: 'warning',
      title: 'Missing Grade',
      description: 'Grade is required for manual entries',
      duration: 4000,
    })
    return
  }

  isCreating.value = true

  try {
    await iliasService.createTestResult(newResult.value)

    toast.add({
      color: 'success',
      title: 'Created',
      description: 'Test result created successfully',
      duration: 3000,
    })

    createDialogVisible.value = false
    await loadResults()
  } catch (error) {
    console.error('Error creating test result:', error)
    toast.add({
      color: 'error',
      title: 'Error',
      description: 'Failed to create test result',
      duration: 5000,
    })
  } finally {
    isCreating.value = false
  }
}

async function deleteResult() {
  if (!deletingResult.value) {
    return
  }

  isDeleting.value = true

  try {
    await iliasService.deleteTestResult(deletingResult.value.id)

    toast.add({
      color: 'success',
      title: 'Deleted',
      description: 'Test result deleted successfully',
      duration: 3000,
    })

    deleteDialogVisible.value = false
    await loadResults()
  } catch (error) {
    console.error('Error deleting test result:', error)
    toast.add({
      color: 'error',
      title: 'Error',
      description: 'Failed to delete test result',
      duration: 5000,
    })
  } finally {
    isDeleting.value = false
  }
}

function clearSyncMessage() {
  syncMessage.value = null
}

function clearSearchFilter() {
  searchFilter.value = ''
}

function clearEligibleLevelFilter() {
  eligibleLevelFilter.value = null
}

// Initialize data on mount
onMounted(() => {
  loadResults()
})
</script>

<template>
  <h1 class="text-3xl">ILIAS Sync Management</h1>

  <div class="flex flex-col gap-3 py-2 md:flex-row md:items-center md:justify-between">
    <div class="flex w-full flex-col gap-3 md:w-2/3 md:flex-row">
      <AppIconField>
        <UInput
          v-model="searchFilter"
          placeholder="Search username or matriculation number"
          class="w-full"
        />
        <AppInputIcon
          v-if="searchFilter"
          name="i-lucide-x"
          class="cursor-pointer"
          @click="clearSearchFilter"
        />
        <AppInputIcon v-else name="i-lucide-search" />
      </AppIconField>
      <AppSelect
        v-model="eligibleLevelFilter"
        :options="languageLevelOptions"
        option-label="label"
        option-value="value"
        placeholder="Filter by eligible level"
        class="w-full md:w-56"
        show-clear
        @clear="clearEligibleLevelFilter"
      />
    </div>
    <div class="flex w-full justify-end gap-3 md:w-auto">
      <UButton
        icon="i-lucide-plus"
        label="Add Manual Entry"
        color="neutral"
        @click="openCreateDialog"
      />
      <UButton icon="i-lucide-download" label="Sync Now" :loading="isSyncing" @click="syncData" />
    </div>
  </div>

  <!-- Sync Status Messages -->
  <div v-if="syncMessage" class="mb-4">
    <AppAlert
      :color="toAppColor(syncSuccess ? 'success' : 'error')"
      :closable="true"
      @close="clearSyncMessage"
    >
      {{ syncMessage }}
    </AppAlert>
  </div>

  <!-- Data Table with Built-in Filtering -->
  <div class="mycard">
    <!-- Loading State -->
    <div v-if="isLoading" class="py-8 text-center">
      <AppSpinner />
      <p class="mt-4 text-gray-600 dark:text-gray-400">Loading testResults...</p>
    </div>

    <!-- HTML Table for Performance -->
    <div v-else class="overflow-x-auto">
      <table
        class="min-w-full rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-neutral-800"
      >
        <!-- Table Header -->
        <thead class="bg-neutral-50 dark:bg-neutral-700">
          <tr>
            <th
              class="cursor-pointer px-4 py-3 text-left text-xs font-semibold tracking-wider text-gray-700 uppercase hover:bg-neutral-100 dark:text-gray-300 dark:hover:bg-neutral-600"
              @click="toggleSort('userName')"
            >
              Username {{ sortField === 'userName' ? (sortOrder === 'asc' ? '↑' : '↓') : '' }}
            </th>
            <th
              class="cursor-pointer px-4 py-3 text-left text-xs font-semibold tracking-wider text-gray-700 uppercase hover:bg-neutral-100 dark:text-gray-300 dark:hover:bg-neutral-600"
              @click="toggleSort('matriculationNumber')"
            >
              Matriculation No.
              {{ sortField === 'matriculationNumber' ? (sortOrder === 'asc' ? '↑' : '↓') : '' }}
            </th>
            <th
              class="cursor-pointer px-4 py-3 text-left text-xs font-semibold tracking-wider text-gray-700 uppercase hover:bg-neutral-100 dark:text-gray-300 dark:hover:bg-neutral-600"
              @click="toggleSort('percent')"
            >
              Score % {{ sortField === 'percent' ? (sortOrder === 'asc' ? '↑' : '↓') : '' }}
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-semibold tracking-wider text-gray-700 uppercase dark:text-gray-300"
            >
              Eligible Levels
            </th>
            <th
              class="cursor-pointer px-4 py-3 text-left text-xs font-semibold tracking-wider text-gray-700 uppercase hover:bg-neutral-100 dark:text-gray-300 dark:hover:bg-neutral-600"
              @click="toggleSort('language')"
            >
              Language {{ sortField === 'language' ? (sortOrder === 'asc' ? '↑' : '↓') : '' }}
            </th>
            <th
              class="cursor-pointer px-4 py-3 text-left text-xs font-semibold tracking-wider text-gray-700 uppercase hover:bg-neutral-100 dark:text-gray-300 dark:hover:bg-neutral-600"
              @click="toggleSort('source')"
            >
              Source {{ sortField === 'source' ? (sortOrder === 'asc' ? '↑' : '↓') : '' }}
            </th>
            <th
              class="cursor-pointer px-4 py-3 text-left text-xs font-semibold tracking-wider text-gray-700 uppercase hover:bg-neutral-100 dark:text-gray-300 dark:hover:bg-neutral-600"
              @click="toggleSort('createdAt')"
            >
              Created {{ sortField === 'createdAt' ? (sortOrder === 'asc' ? '↑' : '↓') : '' }}
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-semibold tracking-wider text-gray-700 uppercase dark:text-gray-300"
            >
              Actions
            </th>
          </tr>
        </thead>

        <!-- Table Body -->
        <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
          <!-- Empty State -->
          <tr v-if="filteredAndSortedResults.length === 0">
            <td colspan="8" class="px-4 py-8 text-center">
              <div class="text-center">
                <UIcon
                  name="i-lucide-inbox"
                  class="mb-4 block text-4xl text-gray-400 dark:text-gray-500"
                />
                <p class="text-gray-600 dark:text-gray-400">No testResults found</p>
              </div>
            </td>
          </tr>

          <!-- Data Rows -->
          <tr
            v-for="result in filteredAndSortedResults"
            :key="result.id"
            class="hover:bg-neutral-50 dark:hover:bg-neutral-700"
          >
            <!-- Username -->
            <td class="px-4 py-3 whitespace-nowrap">
              <span class="font-mono text-sm font-medium text-gray-900 dark:text-gray-100">
                {{ result.userName }}
              </span>
            </td>

            <!-- Matriculation Number -->
            <td class="px-4 py-3 whitespace-nowrap">
              <span
                v-if="result.matriculationNumber"
                class="font-mono text-sm text-gray-600 dark:text-gray-400"
              >
                {{ result.matriculationNumber }}
              </span>
              <span v-else class="text-sm text-gray-400 dark:text-gray-500">—</span>
            </td>

            <!-- Score % -->
            <td class="px-4 py-3 whitespace-nowrap">
              <span
                v-if="result.percent !== null && result.percent !== undefined"
                class="text-sm font-semibold"
                :class="getScoreClass(result.percent)"
              >
                {{ result.percent }}%
              </span>
              <span v-else class="text-sm text-gray-400 dark:text-gray-500">—</span>
            </td>

            <!-- Eligible Levels -->
            <td class="px-4 py-3 whitespace-nowrap">
              <span
                v-if="getEligibleLevels(result).length"
                class="text-sm text-gray-700 dark:text-gray-300"
              >
                {{ getEligibleLevelsLabel(result) }}
              </span>
              <span v-else class="text-gray-400 dark:text-gray-500">—</span>
            </td>

            <!-- Language -->
            <td class="px-4 py-3 whitespace-nowrap">
              <span class="text-sm text-gray-700 dark:text-gray-300">
                {{ t(`enums.spokenLanguage.${result.language}`) }}
              </span>
            </td>

            <!-- Source -->
            <td class="px-4 py-3 whitespace-nowrap">
              <span class="text-sm font-medium text-blue-700 dark:text-blue-400">
                {{ result.source || '—' }}
              </span>
            </td>

            <!-- Created -->
            <td class="px-4 py-3 whitespace-nowrap">
              <span class="text-sm text-gray-600 dark:text-gray-400">
                {{ d(result.createdAt, 'datetime') }}
              </span>
            </td>

            <!-- Actions -->
            <td class="px-4 py-3 whitespace-nowrap">
              <div class="flex gap-2 text-sm">
                <button
                  class="rounded border border-blue-300 bg-blue-100 px-2 py-1 text-xs text-blue-700 transition-colors hover:bg-blue-200 dark:border-blue-700 dark:bg-blue-900 dark:text-blue-300 dark:hover:bg-blue-800"
                  @click="editResult(result)"
                >
                  Edit
                </button>
                <button
                  class="rounded border border-red-300 bg-red-100 px-2 py-1 text-xs text-red-700 transition-colors hover:bg-red-200 dark:border-red-700 dark:bg-red-900 dark:text-red-300 dark:hover:bg-red-800"
                  @click="confirmDelete(result)"
                >
                  Delete
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Results Summary -->
      <div class="mt-4 text-sm text-gray-600 dark:text-gray-400">
        Showing {{ filteredAndSortedResults.length }} of {{ testResults.length }} testResults
      </div>
    </div>
  </div>

  <!-- Edit Dialog -->
  <AppModal
    v-model:open="editDialogVisible"
    :title="`Edit Test Result: ${editingResult?.userName}`"
    :modal="true"
    :closable="true"
    class="w-full max-w-md"
  >
    <div v-if="editingResult" class="flex flex-col gap-4">
      <div>
        <label for="editMatriculationNumber" class="mb-1 block text-sm font-semibold"
          >Matriculation Number</label
        >
        <UInput
          id="editMatriculationNumber"
          v-model="editMatriculationNumber"
          placeholder="Enter matriculation number"
          class="w-full"
        />
      </div>

      <div>
        <label for="editLanguage" class="mb-1 block text-sm font-semibold">Language</label>
        <AppSelect
          id="editLanguage"
          v-model="editingResult.language"
          :options="languageOptions"
          option-label="label"
          option-value="value"
          placeholder="Select language"
          class="w-full"
        />
      </div>

      <div v-if="isEditingGradeBacked">
        <label for="editGrade" class="mb-1 block text-sm font-semibold">Grade</label>
        <AppSelect
          id="editGrade"
          v-model="editingResult.grade"
          :options="languageLevelOptions"
          placeholder="Select grade"
          option-label="label"
          option-value="value"
          class="w-full"
        />
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-2">
        <UButton label="Cancel" color="neutral" @click="editDialogVisible = false" />
        <UButton label="Save" :loading="isSaving" @click="saveResult" />
      </div>
    </template>
  </AppModal>

  <!-- Create Manual Entry Dialog -->
  <AppModal
    v-model:open="createDialogVisible"
    title="Create Manual Entry"
    :modal="true"
    :closable="true"
    class="w-full max-w-md"
  >
    <div class="flex flex-col gap-4">
      <div>
        <label for="createUserName" class="mb-1 block text-sm font-semibold">Username *</label>
        <UInput
          id="createUserName"
          v-model="newResult.userName"
          placeholder="Enter username"
          class="w-full"
          required
        />
      </div>

      <div>
        <label for="createMatriculationNumber" class="mb-1 block text-sm font-semibold"
          >Matriculation Number</label
        >
        <UInput
          id="createMatriculationNumber"
          v-model="newMatriculationNumber"
          placeholder="Enter matriculation number"
          class="w-full"
        />
      </div>

      <div>
        <label for="createLanguage" class="mb-1 block text-sm font-semibold">Language *</label>
        <AppSelect
          id="createLanguage"
          v-model="newResult.language"
          :options="languageOptions"
          option-label="label"
          option-value="value"
          placeholder="Select language"
          class="w-full"
          required
        />
      </div>

      <div>
        <label for="createGrade" class="mb-1 block text-sm font-semibold">Grade *</label>
        <AppSelect
          id="createGrade"
          v-model="newResult.grade"
          :options="languageLevelOptions"
          placeholder="Select grade"
          option-label="label"
          option-value="value"
          class="w-full"
          required
        />
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-2">
        <UButton label="Cancel" color="neutral" @click="createDialogVisible = false" />
        <UButton
          label="Create"
          :loading="isCreating"
          :disabled="!newResult.userName || !newResult.grade || !newResult.language"
          @click="createResult"
        />
      </div>
    </template>
  </AppModal>

  <!-- Delete Confirmation Dialog -->
  <AppModal
    v-model:open="deleteDialogVisible"
    title="Confirm Delete"
    :modal="true"
    :closable="true"
    class="w-full max-w-md"
  >
    <div class="flex items-center gap-3">
      <UIcon name="i-lucide-triangle-alert" class="text-2xl text-orange-700 dark:text-orange-400" />
      <div>
        <p class="font-semibold">Delete result for {{ deletingResult?.userName }}?</p>
        <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">This action cannot be undone.</p>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-2">
        <UButton label="Cancel" color="neutral" @click="deleteDialogVisible = false" />
        <UButton label="Delete" color="error" :loading="isDeleting" @click="deleteResult" />
      </div>
    </template>
  </AppModal>
</template>

<style scoped>
.ilias-testResults-page {
  padding: 1.5rem;
  max-width: 1400px;
  margin: 0 auto;
}
</style>
